// AudioFileBlockEditor — uploaded audio (mp3/m4a) with optional cover image.

import { useState } from "react";
import type { AudioFileBlock, Block, MediaWidth } from "../../../lib/blocks";
import { fileUrl } from "../../../lib/pb";
import { MediaPicker } from "../MediaPicker";
import { t } from "../theme";

export interface AudioFileBlockEditorProps {
  block: AudioFileBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
  articleId: string;
  mediaAssets: string[];
  onUploadFile: (file: File) => Promise<string>;
}

const WIDTHS: MediaWidth[] = ["narrow", "regular", "wide", "full"];

export function AudioFileBlockEditor({
  block,
  onChange,
  isSelected,
  articleId,
  mediaAssets,
  onUploadFile,
}: AudioFileBlockEditorProps) {
  const [pickerOpen, setPickerOpen] = useState<"audio" | "poster" | null>(null);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleDropFile(file: File | null) {
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      setErr("Only audio files");
      return;
    }
    setErr(null);
    try {
      const filename = await onUploadFile(file);
      onChange({ file: filename } as Partial<AudioFileBlock>);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div>
      {isSelected && block.file && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: t.sans, flexWrap: "wrap" }}>
          {WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onChange({ width: w } as Partial<AudioFileBlock>)}
              style={pillStyle((block.width || "regular") === w)}
            >
              {w}
            </button>
          ))}
        </div>
      )}

      {block.file ? (
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            padding: 10,
            border: `1px solid ${t.border}`,
            borderRadius: 6,
            background: t.bg,
          }}
        >
          {block.poster && (
            <img
              src={fileUrl("articles", articleId, block.poster, "120x120")}
              alt=""
              style={{
                width: 64,
                height: 64,
                objectFit: "cover",
                borderRadius: 4,
                background: t.cardRaised,
              }}
            />
          )}
          <audio
            src={fileUrl("articles", articleId, block.file)}
            controls
            style={{ flex: 1 }}
          />
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files?.[0];
            void handleDropFile(f || null);
          }}
          onClick={() => setPickerOpen("audio")}
          style={{
            padding: "32px 20px",
            borderRadius: 8,
            border: `2px dashed ${drag ? t.accent : t.border}`,
            background: drag ? t.accentSoft : t.bg,
            color: t.text3,
            textAlign: "center",
            cursor: "pointer",
            fontFamily: t.sans,
            fontSize: 13,
            transition: t.transition,
          }}
        >
          <div style={{ fontSize: 20, color: t.text4, marginBottom: 6 }}>♪</div>
          Drop an audio file here or click to choose
        </div>
      )}

      {err && (
        <div style={{ marginTop: 6, fontSize: 11, color: t.danger, fontFamily: t.sans }}>
          {err}
        </div>
      )}

      {isSelected && block.file && (
        <div style={{ display: "flex", gap: 6, marginTop: 8, fontFamily: t.sans, flexWrap: "wrap" }}>
          <button type="button" onClick={() => setPickerOpen("audio")} style={ghostBtnStyle}>
            Replace audio…
          </button>
          <button type="button" onClick={() => setPickerOpen("poster")} style={ghostBtnStyle}>
            {block.poster ? "Replace cover…" : "Set cover…"}
          </button>
          {block.poster && (
            <button
              type="button"
              onClick={() => onChange({ poster: undefined } as Partial<AudioFileBlock>)}
              style={ghostBtnStyle}
            >
              Clear cover
            </button>
          )}
        </div>
      )}

      {isSelected && (
        <input
          type="text"
          value={block.caption || ""}
          onChange={(e) =>
            onChange({ caption: e.target.value } as Partial<AudioFileBlock>)
          }
          placeholder="Caption (optional)"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: 8,
            padding: "6px 8px",
            background: t.bg,
            border: `1px solid ${t.border}`,
            borderRadius: 4,
            fontSize: 12,
            color: t.text,
            outline: "none",
            fontFamily: t.sans,
          }}
        />
      )}

      {pickerOpen && (
        <MediaPicker
          articleId={articleId}
          mediaAssets={mediaAssets}
          accept={pickerOpen === "poster" ? "image" : "any"}
          onPick={(filename) => {
            if (pickerOpen === "poster") {
              onChange({ poster: filename } as Partial<AudioFileBlock>);
            } else {
              onChange({ file: filename } as Partial<AudioFileBlock>);
            }
            setPickerOpen(null);
          }}
          onClose={() => setPickerOpen(null)}
          onUploadFile={onUploadFile}
        />
      )}
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    cursor: "pointer",
  };
}

const ghostBtnStyle: React.CSSProperties = {
  padding: "5px 10px",
  background: "transparent",
  border: `1px solid ${t.border}`,
  borderRadius: 4,
  fontSize: 11,
  color: t.text2,
  cursor: "pointer",
  fontFamily: t.sans,
};
